import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { hasPermission, hasAllPermissions, hasAnyPermissions } from '../utils/permission.util';

export interface ProtectedRouteProps {
  requiredRole?: string;
  requiredPermission?: string | string[];
  permissionCheckType?: 'all' | 'any';
  fallbackPath?: string;
  children: React.ReactNode;
}

/**
 * ProtectedRoute component that guards routes based on role and permissions.
 * SuperAdmin always has access regardless of role/permission requirements.
 * Usage: <Route path="/x" element={<ProtectedRoute requiredPermission="/users"><Page /></ProtectedRoute>} />
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  requiredRole,
  requiredPermission,
  permissionCheckType = 'any',
  fallbackPath = '/dashboard',
  children,
}) => {
  const { user, isAuthenticated, isSuperAdmin, permissions, isLoading } = useAuth();

  if (isLoading) {
    return <div>Loading...</div>;
  }

  let hasAccess = isAuthenticated && !!user;

  // SuperAdmin always has full access
  if (hasAccess && isSuperAdmin) {
    return <>{children}</>;
  }

  if (hasAccess && requiredRole && user?.role !== requiredRole) {
    hasAccess = false;
  }

  if (hasAccess && requiredPermission) {
    if (Array.isArray(requiredPermission)) {
      hasAccess = permissionCheckType === 'all'
        ? hasAllPermissions(requiredPermission, permissions || [])
        : hasAnyPermissions(requiredPermission, permissions || []);
    } else {
      hasAccess = hasPermission(requiredPermission, permissions || []);
    }
  }

  if (!hasAccess) {
    return <Navigate to={fallbackPath} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
